import { useMutation } from '@tanstack/react-query';
import { TRequestHandlerError } from '@studio-toolkit/request-handler';

import { useQuantum } from 'hooks';
import { useTokenAndHeaders } from 'hooks/useHeaders';

import { apiUrl, requestHandler } from 'utils';

// Define your PdfInput type according to your backend schema
interface PdfInput {
  pickedPlatform: string;
  orderType?: string;
  customerSegment?: string;
  servicesRepresented?: string;
  firstName?: string;
  accountNumber?: string;
  facility?: string;
  /**
   * Collateral version to generate, e.g. "1.0.0". The backend forwards this as
   * a URL path segment. Omit to get the latest/unversioned collateral.
   */
  versionRequested?: string;
}

interface ResponsePdf {
  status: number;
  message: string;
  pdf?: string;
}

export interface IUseGeneratePdfBase64ReturnValues {
  generatePdf: (input: PdfInput) => Promise<ResponsePdf>;
  pdfResponse: ResponsePdf | undefined;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  reset: () => void;
}

const useGeneratePdfBase64 = (): IUseGeneratePdfBase64ReturnValues => {
  const { trackApiCall } = useQuantum();

  const url = apiUrl({
    endpoint: 'generatePdf',
    apiEnv: import.meta.env.VITE_APP_ENV,
  });
  const { headers } = useTokenAndHeaders();

  const {
    mutateAsync: generatePdf,
    data,
    error,
    isPending,
    isError,
    isSuccess,
    reset,
  } = useMutation<ResponsePdf, TRequestHandlerError, PdfInput>({
    mutationKey: ['generatePdfBase64'],
    mutationFn: async (input) => {
      const res = (await requestHandler({
        requestConfig: {
          method: 'POST',
          url: url,
          data: input,
          headers,
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            console.log(error);
            throw new Error('PDF generation failed');
          },
        },
        quantumHandler: trackApiCall,
      })) as ResponsePdf;

      if (res.status !== 200 || !res?.pdf) {
        throw new Error('PDF generation failed');
      }

      return res;
    },
  });

  return {
    generatePdf,
    pdfResponse: data,
    error,
    isLoading: isPending,
    isError,
    isSuccess,
    reset,
  };
};

export default useGeneratePdfBase64;
